import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Users,
  Phone,
  Envelope,
  InstagramLogo,
  Cake,
  CalendarBlank,
  CurrencyDollar,
  UserCircle,
  Scissors as ScissorsIcon,
  ArrowLeft,
  PencilSimple,
  Trash,
  Plus,
  Star,
  ShoppingBag,
  Clock,
  Scissors,
  X,
  FloppyDisk,
  Spinner,
  NoteBlank,
} from '@phosphor-icons/react';
import { API, STATUS_CONFIG, formatCurrency, formatDate, formatDateRelative, formatPhone } from './constants';
import { ClienteFormModal } from './ClienteForm';
import { FichaTecnicaSection } from './FichaTecnica';

const VisitaFormInline = ({ clienteId, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    data: new Date().toISOString().split('T')[0],
    profissional: '',
    servicos: '',
    produtos: '',
    valor: '',
    observacoes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const servicos = form.servicos
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const produtos = form.produtos
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      const valor = parseFloat(form.valor.replace(',', '.')) || 0;

      const res = await fetch(`${API}/clientes/${clienteId}/visitas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: clienteId,
          data: form.data,
          profissional: form.profissional,
          servicos,
          produtos,
          valor,
          observacoes: form.observacoes,
        }),
      });
      if (!res.ok) throw new Error('Erro');
      onSuccess();
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-5 bg-[#F7F6F4] rounded-2xl border border-[#E4E4E7] space-y-4" data-testid="visita-form">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Registrar Visita</h4>
        <button type="button" onClick={onCancel} className="text-[#A1A1AA] hover:text-[#18181B]">
          <X size={18} weight="bold" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data</label>
          <input
            data-testid="visita-data"
            type="date"
            value={form.data}
            onChange={(e) => setForm(p => ({ ...p, data: e.target.value }))}
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional</label>
          <input
            data-testid="visita-profissional"
            type="text"
            value={form.profissional}
            onChange={(e) => setForm(p => ({ ...p, profissional: e.target.value }))}
            placeholder="Nome"
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Servicos (separar por virgula)</label>
        <input
          data-testid="visita-servicos"
          type="text"
          value={form.servicos}
          onChange={(e) => setForm(p => ({ ...p, servicos: e.target.value }))}
          placeholder="Corte, Escova, Coloracao..."
          className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Produtos (separar por virgula)</label>
          <input
            data-testid="visita-produtos"
            type="text"
            value={form.produtos}
            onChange={(e) => setForm(p => ({ ...p, produtos: e.target.value }))}
            placeholder="Shampoo, Mascara..."
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Valor (R$)</label>
          <input
            data-testid="visita-valor"
            type="text"
            value={form.valor}
            onChange={(e) => setForm(p => ({ ...p, valor: e.target.value }))}
            placeholder="150,00"
            className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Observacoes</label>
        <input
          data-testid="visita-observacoes"
          type="text"
          value={form.observacoes}
          onChange={(e) => setForm(p => ({ ...p, observacoes: e.target.value }))}
          placeholder="Opcional"
          className="w-full px-3 py-2.5 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="btn-salvar-visita"
        className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition-all hover:shadow-md disabled:opacity-60"
        style={{ backgroundColor: '#1B4332' }}
      >
        {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
        {saving ? 'Salvando...' : 'Salvar Visita'}
      </button>
    </form>
  );
};

export const ClienteFicha = () => {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showVisitaForm, setShowVisitaForm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchCliente = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/clientes/${clienteId}`);
      if (!res.ok) throw new Error('Not found');
      const data = await res.json();
      setCliente(data);
    } catch {
      setCliente(null);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchCliente();
  }, [fetchCliente]);

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta cliente?')) return;
    setDeleting(true);
    try {
      await fetch(`${API}/clientes/${clienteId}`, { method: 'DELETE' });
      navigate('/gestao/clientes');
    } catch {
      setDeleting(false);
    }
  };

  const handleDeleteVisita = async (visitaId) => {
    if (!window.confirm('Excluir esta visita?')) return;
    try {
      await fetch(`${API}/clientes/${clienteId}/visitas/${visitaId}`, { method: 'DELETE' });
      fetchCliente();
    } catch {
      // silently fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="min-h-screen bg-[#F7F6F4] flex items-center justify-center">
        <div className="text-center">
          <p className="salonup-heading-md mb-4">Cliente nao encontrada</p>
          <button onClick={() => navigate('/gestao/clientes')} className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /> Voltar
          </button>
        </div>
      </div>
    );
  }

  const st = STATUS_CONFIG[cliente.status] || STATUS_CONFIG.novo;

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="cliente-ficha-page">
      <div className="salonup-container max-w-4xl">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark">
              <Scissors size={22} weight="bold" />
            </div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao/clientes')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" />
            <span>Voltar</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-6 animate-fade-in opacity-0 delay-100" style={{ animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332] transition-colors" onClick={() => navigate('/gestao/clientes')}>Clientes</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">{cliente.nome}</span>
          </p>
        </div>

        {/* Profile Header */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
              {/* Avatar */}
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold flex-shrink-0"
                style={{ backgroundColor: st.color, fontFamily: 'Outfit' }}
              >
                {cliente.nome?.charAt(0)?.toUpperCase() || '?'}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit', letterSpacing: '-0.02em' }}>
                    {cliente.nome}
                  </h1>
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-lg uppercase tracking-wide"
                    style={{ backgroundColor: st.bg, color: st.color }}
                  >
                    {st.label}
                  </span>
                  {cliente.codigo_cliente && (
                    <span
                      data-testid="ficha-codigo-cliente"
                      className="font-mono text-xs text-[#71717A] bg-[#F4F4F5] border border-[#E4E4E7] rounded-md px-2 py-0.5"
                      title="Codigo operacional do cliente"
                    >
                      {cliente.codigo_cliente}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-[#71717A]">
                  {cliente.telefone && (
                    <span className="flex items-center gap-1.5">
                      <Phone size={14} weight="regular" />
                      {formatPhone(cliente.telefone)}
                    </span>
                  )}
                  {cliente.email && (
                    <span className="flex items-center gap-1.5">
                      <Envelope size={14} weight="regular" />
                      {cliente.email}
                    </span>
                  )}
                  {cliente.instagram && (
                    <span className="flex items-center gap-1.5">
                      <InstagramLogo size={14} weight="regular" />
                      {cliente.instagram}
                    </span>
                  )}
                  {cliente.aniversario && (
                    <span className="flex items-center gap-1.5">
                      <Cake size={14} weight="regular" />
                      {formatDate(cliente.aniversario)}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid="btn-editar-cliente"
                  onClick={() => setShowEditForm(true)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5] hover:text-[#18181B] transition-all"
                >
                  <PencilSimple size={18} weight="bold" />
                </button>
                <button
                  data-testid="btn-excluir-cliente"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="w-10 h-10 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all"
                >
                  <Trash size={18} weight="bold" />
                </button>
              </div>
            </div>

            {/* Observacoes */}
            {cliente.observacoes && (
              <div className="mt-5 pt-5 border-t border-[#E4E4E7]">
                <div className="flex items-start gap-2">
                  <NoteBlank size={14} weight="regular" className="text-[#A1A1AA] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[#71717A] leading-relaxed">{cliente.observacoes}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="stat-visitas">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} weight="regular" className="text-[#A1A1AA]" />
              <span className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Visitas</span>
            </div>
            <p className="text-2xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{cliente.total_visitas}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">
              {cliente.ultima_visita ? `Ultima: ${formatDateRelative(cliente.ultima_visita)}` : 'Nenhuma visita'}
            </p>
          </div>

          <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="stat-ticket">
            <div className="flex items-center gap-2 mb-3">
              <CurrencyDollar size={16} weight="regular" className="text-[#A1A1AA]" />
              <span className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Ticket Medio</span>
            </div>
            <p className="text-2xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(cliente.ticket_medio)}</p>
            <p className="text-xs text-[#A1A1AA] mt-1">por visita</p>
          </div>

          <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="stat-profissional">
            <div className="flex items-center gap-2 mb-3">
              <UserCircle size={16} weight="regular" className="text-[#A1A1AA]" />
              <span className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Profissional</span>
            </div>
            <p className="text-lg font-semibold text-[#18181B] truncate" style={{ fontFamily: 'Outfit' }}>
              {cliente.profissional_top || '-'}
            </p>
            <p className="text-xs text-[#A1A1AA] mt-1">mais recorrente</p>
          </div>

          <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="stat-status">
            <div className="flex items-center gap-2 mb-3">
              <Star size={16} weight="regular" className="text-[#A1A1AA]" />
              <span className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Status</span>
            </div>
            <span
              className="text-lg font-semibold"
              style={{ color: st.color, fontFamily: 'Outfit' }}
            >
              {st.label}
            </span>
            <p className="text-xs text-[#A1A1AA] mt-1">classificacao atual</p>
          </div>
        </section>

        {/* Two Columns: Servicos + Produtos */}
        {(cliente.servicos_top?.length > 0 || cliente.produtos_comprados?.length > 0) && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 animate-fade-in-up opacity-0" style={{ animationFillMode: 'forwards', animationDelay: '250ms' }}>
            {/* Top Servicos */}
            {cliente.servicos_top?.length > 0 && (
              <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="servicos-top">
                <div className="flex items-center gap-2 mb-4">
                  <ScissorsIcon size={16} weight="regular" className="text-[#1B4332]" />
                  <span className="text-xs font-semibold text-[#18181B] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
                    Servicos Recorrentes
                  </span>
                </div>
                <div className="space-y-2.5">
                  {cliente.servicos_top.map((s, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-[#18181B]">{s.nome}</span>
                      <span className="text-xs font-medium text-[#A1A1AA] bg-[#F4F4F5] px-2 py-0.5 rounded-md">
                        {s.count}x
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Produtos */}
            {cliente.produtos_comprados?.length > 0 && (
              <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="produtos-top">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingBag size={16} weight="regular" className="text-[#1B4332]" />
                  <span className="text-xs font-semibold text-[#18181B] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
                    Produtos Comprados
                  </span>
                </div>
                <div className="space-y-2.5">
                  {cliente.produtos_comprados.map((p, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-[#18181B]">{p.nome}</span>
                      <span className="text-xs font-medium text-[#A1A1AA] bg-[#F4F4F5] px-2 py-0.5 rounded-md">
                        {p.count}x
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Ficha Tecnica */}
        <FichaTecnicaSection cliente={cliente} onRefresh={fetchCliente} />

        {/* Visitas Section */}
        <section className="mb-8 animate-fade-in-up opacity-0" style={{ animationFillMode: 'forwards', animationDelay: '300ms' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
              Historico de Visitas
            </h2>
            {!showVisitaForm && (
              <button
                data-testid="btn-nova-visita"
                onClick={() => setShowVisitaForm(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium text-white transition-all hover:shadow-md"
                style={{ backgroundColor: '#1B4332' }}
              >
                <Plus size={14} weight="bold" />
                Nova Visita
              </button>
            )}
          </div>

          {showVisitaForm && (
            <div className="mb-4">
              <VisitaFormInline
                clienteId={clienteId}
                onSuccess={() => { setShowVisitaForm(false); fetchCliente(); }}
                onCancel={() => setShowVisitaForm(false)}
              />
            </div>
          )}

          {cliente.visitas?.length > 0 ? (
            <div className="space-y-2">
              {cliente.visitas.map((v) => (
                <div key={v.id} data-testid={`visita-${v.id}`} className="salonup-card p-4 cursor-default hover:transform-none">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-1.5">
                        <span className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>
                          {formatDate(v.data)}
                        </span>
                        {v.profissional && (
                          <span className="text-xs text-[#71717A] bg-[#F4F4F5] px-2 py-0.5 rounded-md">
                            {v.profissional}
                          </span>
                        )}
                        {v.valor > 0 && (
                          <span className="text-xs font-semibold text-[#1B4332]">
                            {formatCurrency(v.valor)}
                          </span>
                        )}
                      </div>
                      {v.servicos?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1">
                          {v.servicos.map((s, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-[#1B4332]/8 text-[#1B4332] font-medium">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                      {v.produtos?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {v.produtos.map((p, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-[#C9A962]/15 text-[#8B7340] font-medium">
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                      {v.observacoes && (
                        <p className="text-xs text-[#A1A1AA] mt-1.5">{v.observacoes}</p>
                      )}
                    </div>
                    <button
                      data-testid={`btn-delete-visita-${v.id}`}
                      onClick={() => handleDeleteVisita(v.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:bg-red-50 hover:text-red-500 transition-all flex-shrink-0"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !showVisitaForm && (
              <div className="salonup-card p-8 text-center cursor-default hover:transform-none">
                <CalendarBlank size={32} weight="light" className="mx-auto mb-3 text-[#A1A1AA]" />
                <p className="text-sm text-[#71717A]">Nenhuma visita registrada</p>
                <button
                  onClick={() => setShowVisitaForm(true)}
                  className="mt-3 text-xs font-medium text-[#1B4332] hover:underline"
                >
                  Registrar primeira visita
                </button>
              </div>
            )
          )}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
          <p className="salonup-text-xs">Gestao (CORE) &middot; Clientes &middot; Ficha Viva</p>
        </footer>
      </div>

      {/* Edit Modal */}
      {showEditForm && (
        <ClienteFormModal
          cliente={cliente}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => { setShowEditForm(false); fetchCliente(); }}
        />
      )}
    </div>
  );
};

export default ClienteFicha;
